<template>
    <TooltipComp
        :text="powerTooltip"
        placement="right"
    >
        <IconComp
            v-if="!present"
            class="mt-2"
            icon="fa-ban"
            family="fa-duotone"
            transform="rotate-180 shrink-2 up-1"
        />
        <span
            v-else
            class="position-relative"
        >
            <IconComp
                class="mt-2"
                :class="[powerColor, { 'opacity-75': !active }]"
                :icon="powerIcon"
                family="fa-duotone"
            ></IconComp>
        </span>
    </TooltipComp>
</template>
<script lang="ts" setup>
import { IconComp, TooltipComp } from '@/components';
import { $tm } from '@/locize';
import { computed } from 'vue';

const props = withDefaults(
    defineProps<{
        batteryLevel: number | null;

        /**
         * If the battery is active or not
         * @default true
         */
        active?: boolean;

        /**
         * If the battery is present or not
         * @default true
         */
        present?: boolean;

        /**
         * If the tooltip should be shown or not
         * The tooltip shows the battery level
         * @default false
         */
        tooltip?: boolean;
    }>(),
    {
        active: true,
        tooltip: false,
        present: true,
    },
);

// Listen to changes to the battery level and set a new icon
const powerIcon = computed(() => {
    return getPowerIcon(props.batteryLevel);
});

// Listen to changes to the battery level and set a new color
const powerColor = computed(() => {
    return getPowerColor(props.batteryLevel);
});

const powerTooltip = computed(() => {
    if (!props.tooltip) return '';

    if (props.batteryLevel === null) return $tm('device.batterylevelunknown');

    return $tm('device.batterylevel', props.batteryLevel ?? 0);
});

/**
 * Select the correct icon from the power level.
 * @param powerlevel
 */
const getPowerIcon = (powerlevel: number | null): string => {
    if (powerlevel === null) return 'fa-battery-bolt';

    if (powerlevel > 90) {
        return 'fa-battery-full';
    } else if (powerlevel > 60) {
        return 'fa-battery-three-quarters';
    } else if (powerlevel > 30) {
        return 'fa-battery-half';
    } else if (powerlevel > 10) {
        return 'fa-battery-low';
    }

    return 'fa-battery-exclamation';
};

/**
 * Select the correct color from the power level.
 * @param powerlevel
 */
const getPowerColor = (powerlevel: number | null): string => {
    if (powerlevel === null) return '';

    if (powerlevel > 90) {
        return 'text-success-emphasis';
    } else if (powerlevel > 60) {
        return '';
    } else if (powerlevel > 30) {
        return '';
    } else if (powerlevel > 10) {
        return 'text-warning-emphasis';
    }

    return 'text-danger';
};
</script>
